import React from 'react';

import { registerClass } from '../SatCoreRegistry';

import courseResourceFilterManager from '../managers/CourseResourceFilterManager';
import { ELEMENT_TYPE } from '../managers/CourseManager';

export default class CourseResourceFilterService {
  /**
   * Clear course resource filters if navigating to a different element and filters are active
   * @param {string} newElementId - The element ID being navigated to
   * @param {string} currentElementId - The current element ID
   * @returns {boolean} True if filters were cleared
   */
  static clearCourseResourceFiltersIfApplicable(newElementId, currentElementId) {
    const selectedTagIds = courseResourceFilterManager.selectedCourseResourceFilterTagIds;
    if (newElementId !== currentElementId && selectedTagIds?.length) {
      courseResourceFilterManager.clearAll();
      return true;
    }
    return false;
  }

  /**
   * DEBUG: Render filter state debug output showing selected tag IDs and available categories
   * Bolds tag IDs that are currently selected in the available categories list
   * @param {string[]} selectedTagIds - Array of currently selected tag IDs
   */
  static DEBUG__renderFilterState(selectedTagIds) {
    return (
      <div className='course-resource-filter-debug-state'>
        <div>
          <strong>selectedCourseResourceFilterTagIds:</strong>
          {' '}
          [
          {courseResourceFilterManager.selectedCourseResourceFilterTagIds?.map((tagId, idx) => (
            <span key={tagId} className='selected-tag-id'>
              &quot;{tagId}&quot;{idx < courseResourceFilterManager.selectedCourseResourceFilterTagIds.length - 1 ? ', ' : ''}
            </span>
          ))}
          ]
        </div>
        <div className='category-list'>
          <strong>availableCourseResourceFilterCategories:</strong>
          {' '}
          [
          {courseResourceFilterManager.availableCourseResourceFilterCategories?.map((category, categoryIndex) => (
            <span key={category.id || categoryIndex}>
              {'{'}name: &quot;{category.name}&quot;, tags: [
              {category.tags?.map((tag, tagIndex) => {
                const isSelected = selectedTagIds.includes(tag.id);
                return (
                  <span key={tag.id} className={isSelected ? 'tag-selected' : 'tag-not-selected'}>
                    {'{'}id: &quot;{tag.id}&quot;, name: &quot;{tag.name}&quot;{'}'}
                    {tagIndex < category.tags.length - 1 ? ', ' : ''}
                  </span>
                );
              })}
              ]{'}'}
              {categoryIndex < courseResourceFilterManager.availableCourseResourceFilterCategories.length - 1 ? ', ' : ''}
            </span>
          ))}
          ]
        </div>
      </div>
    );
  }

  /**
   * DEBUG: Render element tag debug output showing element index, name, type, and tag IDs
   * Bolds tag IDs that match currently selected filters
   * Bolds the index if element has any selected tags
   * @param {Object} courseElement - The course element to display tags for
   * @param {string[]} selectedTagIds - Array of currently selected tag IDs
   * @param {number} index - The index of this element in the list
   */
  static DEBUG__renderElementTag(courseElement, selectedTagIds, index) {
    const hasSelectedTags = courseElement.contentItemTagIds?.some((tagId) => {
      return selectedTagIds.includes(tagId);
    });

    return (
      <div className='course-resource-filter-debug-element'>
        <span style={{ fontWeight: hasSelectedTags ? 'bold' : 'normal' }}>[{index}]</span>
        {' '}
        <strong>{courseElement.name || courseElement.title}</strong>
        {' '}
        ({ELEMENT_TYPE.isBranchType(courseElement) ? 'BRANCH' : 'LEAF'})
        {' - '}
        Tags: [
        {courseElement.contentItemTagIds?.length > 0 ? (
          courseElement.contentItemTagIds.map((tagId, idx) => {
            const isSelected = selectedTagIds.includes(tagId);
            return (
              <span key={tagId} className={isSelected ? 'tag-selected' : 'tag-not-selected'}>
                {tagId}{idx < courseElement.contentItemTagIds.length - 1 ? ', ' : ''}
              </span>
            );
          })
        ) : (
          <span className='no-tags'>none</span>
        )}
        ]
      </div>
    );
  }
}

registerClass('CourseResourceFilterService', CourseResourceFilterService);
